import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaImage, FaRedo, FaUpload } from "react-icons/fa";
import {
  DEFAULT_PLATFORM_LOGO_PATH,
  PLATFORM_LOGO_SETTING_KEY,
  fetchPlatformLogoSetting,
  getDefaultPlatformLogoUrl,
  resolvePlatformLogoUrl,
  savePlatformLogoUrl,
  uploadPlatformLogoImage,
} from "../../services/platformLogo.service";
import { platformManagementExtractMessage } from "../../services/platformManagement.service";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_auth_hero.scss";
import "../../assets/styles/super_admin_platform_logo.scss";

const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const STORED_MAX_KB = 100;

export default function SuperAdminPlatformLogoManagementPage() {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setting, setSetting] = useState(null);
  const [draftPath, setDraftPath] = useState(DEFAULT_PLATFORM_LOGO_PATH);
  const [previewUrl, setPreviewUrl] = useState(getDefaultPlatformLogoUrl);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const row = await fetchPlatformLogoSetting();
      setSetting(row);
      const path = String(row?.settingValue || DEFAULT_PLATFORM_LOGO_PATH).trim();
      setDraftPath(path || DEFAULT_PLATFORM_LOGO_PATH);
      setPreviewUrl(resolvePlatformLogoUrl(path || DEFAULT_PLATFORM_LOGO_PATH));
    } catch (err) {
      toast.error(platformManagementExtractMessage(err, "Could not load platform logo setting."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const validateFile = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return false;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("Platform logo upload must be 10 MB or smaller.");
      return false;
    }
    return true;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !validateFile(file)) return;

    const blobPreview = URL.createObjectURL(file);
    setPreviewUrl(blobPreview);
    setUploading(true);

    try {
      const imagePath = await uploadPlatformLogoImage(file);
      const updated = await savePlatformLogoUrl(imagePath);
      setSetting(updated);
      const savedPath = String(updated?.settingValue || imagePath).trim();
      setDraftPath(savedPath);
      setPreviewUrl(resolvePlatformLogoUrl(savedPath));
      toast.success(
        `Platform logo updated. Light background removed and image optimized to under ${STORED_MAX_KB} KB.`
      );
    } catch (err) {
      setPreviewUrl(resolvePlatformLogoUrl(draftPath));
      toast.error(platformManagementExtractMessage(err, "Platform logo upload failed."));
    } finally {
      URL.revokeObjectURL(blobPreview);
      setUploading(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const updated = await savePlatformLogoUrl(DEFAULT_PLATFORM_LOGO_PATH);
      setSetting(updated);
      const savedPath = String(updated?.settingValue || DEFAULT_PLATFORM_LOGO_PATH).trim();
      setDraftPath(savedPath);
      setPreviewUrl(resolvePlatformLogoUrl(savedPath));
      toast.success("Platform logo reset to default.");
    } catch (err) {
      toast.error(platformManagementExtractMessage(err, "Could not reset platform logo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-auth-hero-page sa-platform-logo-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li aria-current="page">Platform Logo</li>
        </ol>
      </nav>

      <header className="mca-hero">
        <div>
          <div className="mca-hero__title-row">
            <h1 className="mca-hero__title">Platform Logo</h1>
            <span className="mca-hero__badge">Shared</span>
          </div>
          <p className="mca-hero__subtitle">
            One logo used across headers, auth pages, CMS pages, invoices, and the rest of the platform.
          </p>
        </div>
      </header>

      <section className="mca-card sa-auth-hero-page__card">
        <div className="sa-auth-hero-page__intro">
          <span className="sa-auth-hero-page__intro-icon" aria-hidden>
            <FaImage />
          </span>
          <div>
            <h2 className="mca-section-title">Platform brand logo</h2>
            <p className="sa-auth-hero-page__copy">
              Upload any JPG, PNG, or WEBP up to 10 MB. Light backgrounds are removed automatically
              and the logo is optimized to under {STORED_MAX_KB} KB for fast loading across the platform.
              Use a white or light wordmark on transparent or solid backgrounds for dark headers.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="sa-auth-hero-page__status">Loading current logo…</p>
        ) : (
          <>
            <div
              className="sa-platform-logo-page__preview"
              aria-label="Platform logo preview"
            >
              <img src={previewUrl} alt="Platform logo preview" className="sa-platform-logo-page__preview-img" />
              <div className="sa-platform-logo-page__preview-caption">Platform logo preview</div>
            </div>

            <div className="sa-auth-hero-page__meta">
              <strong>Setting key:</strong> {PLATFORM_LOGO_SETTING_KEY}
              {setting?.id != null ? (
                <span className="sa-auth-hero-page__path">Setting id: {setting.id}</span>
              ) : null}
              <span className="sa-auth-hero-page__path">{draftPath}</span>
            </div>

            <div className="sa-auth-hero-page__actions">
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                className="sa-auth-hero-page__file-input"
                onChange={handleFileChange}
              />
              <button
                type="button"
                className="mca-btn mca-btn--secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || saving}
              >
                <FaUpload aria-hidden />
                {uploading ? "Uploading…" : "Upload logo"}
              </button>
              <button
                type="button"
                className="mca-btn mca-btn--secondary"
                onClick={handleReset}
                disabled={uploading || saving}
              >
                <FaRedo aria-hidden />
                {saving ? "Resetting…" : "Reset to default"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
